import React, { useEffect } from 'react'
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useNavigate,
  useLocation
} from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { jwtDecode } from 'jwt-decode'
import { fetchUser, logout } from '../redux/actions/authAction'
import { fetchAppPreferences } from '../redux/actions/userAction'
import { loadOnboarding } from '../features/onboarding/redux/onboardingActions'
import { selectIsOnboardingActive } from '../features/onboarding/redux/onboardingSelectors'
import OnboardingDashboard from '../features/onboarding/components/OnboardingDashboard'
import DashboardScreen from '../screens/DashboardScreen/DashboardScreen'
import SearchScreen from '../screens/SearchScreen/SearchScreen'
import ChatScreen from '../screens/ChatScreen/ChatScreen'
import SettingsScreen from '../screens/SettingsScreen/SettingsScreen'
import LoginScreen from '../screens/LoginScreen/LoginScreen'
import Navbar, { MOBILE_APP_BAR_HEIGHT } from '../components/Navbar'
import NotFoundScreen from '../screens/NotFoundScreen/NotFoundScreen'
import SignupScreen from '../screens/SingupScreen/SignupScreen'
import SignupConfirmationScreen from '../screens/SingupScreen/SignupConfirmationScreen'
import ForgotPasswordScreen from '../screens/LoginScreen/ForgotPasswordScreen'
import ResetPasswordScreen from '../screens/LoginScreen/ResetPasswordScreen'
import ConfirmAccount from '../screens/SingupScreen/ConfirmAccount'
import SignupConfirmationDomainScreen from '../screens/SingupScreen/SignupConfirmationDomainScreen'
import ContractHistory from '../screens/ContractScreen/ContractHistory'
import CLMDashboardScreen from '../screens/ContractScreen/CLMDashboardScreen'
import ContractEditionScreen from '../screens/ContractScreen/ContractEditionScreen'
import TemplateEditorScreen from '../screens/ContractScreen/TemplateEditorScreen'
import WelcomeScreen from '../screens/LoginScreen/WelcomeScreen'
import PaymentSuccessScreen from '../screens/PaymentScreen/PaymentSuccessScreen'
import PaymentCancelScreen from '../screens/PaymentScreen/PaymentCancelScreen'
import PaymentFailedScreen from '../screens/PaymentScreen/PaymentFailedScreen'
import SubscribeScreen from '../screens/SubscribeScreen/SubscribeScreen'
import PasswordResetSuccessScreen from '../screens/LoginScreen/PasswordResetSuccessScreen'
import OTPVerificationScreen from '../screens/OTPVerificationScreen/OTPVerificationScreen'
import { CircularProgress, useTheme, useMediaQuery } from '@mui/material'
import InvitationScreen from '../screens/SingupScreen/InvitationScreen'
import ExternalInvitationScreen from '../screens/SingupScreen/ExternalInvitationScreen'
import FloatingActionButton from '../components/FloatingActionButton'
import { isMobile } from '../utils/utils'
import OnboardingScreen from '../screens/OnboardingScreen/OnboardingScreen'
import NewsHubScreen from '../screens/NewsHubScreen/NewsHubScreen'
import NewsPreferencesScreen from '../screens/NewsHubScreen/NewsPreferencesScreen'
import NewsDetailScreen from '../screens/NewsHubScreen/NewsDetailScreen'
import EntreprisesScreen from '../screens/EntreprisesScreen/EntreprisesScreen'
import CompanyProfileScreen from '../screens/EntreprisesScreen/CompanyProfileScreen'
import AnnonceDetailScreen from '../screens/EntreprisesScreen/AnnonceDetailScreen'
import AdminPanelScreen from '../screens/AdminPanelScreen/AdminPanelScreen'
import SigningScreen from '../screens/SigningScreen/SigningScreen'
import { FEATURES } from '../contexts/SubscriptionProvider'
import { usePageTracking } from '../analytics'
// Subscription validation component — reads from Redux, no extra API calls
const SubscriptionRoute = ({ children }) => {
  const { data: subscription, loading } = useSelector(state => state.subscription)
  const userData = useSelector(state => state.auth.user?.user)
  const location = useLocation()
  const isOnboardingActive = useSelector(selectIsOnboardingActive)

  // These pages are always accessible (bypass subscription + onboarding gates)
  const paymentRelatedPages = [
    '/settings',
    '/checkout',
    '/payment-success',
    '/payment-cancel',
    '/payment-failed',
    '/onboarding',
    '/subscribe',
  ]
  const isPaymentRelatedPage = paymentRelatedPages.some(page =>
    location.pathname === page || location.pathname.startsWith(page + '/')
  )

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress size={42} />
      </div>
    )
  }

  if (isPaymentRelatedPage) return children

  // Super admins bypass all subscription checks
  const isSuperAdmin = userData?.roles?.includes('super_admin')
  if (isSuperAdmin) return children

  const hasValidSubscription = () => {
    if (!subscription) return false
    // Trial: valid only if trial_end is in the future
    if (subscription.status === 'trial') {
      if (subscription.trial_end && new Date(subscription.trial_end) < new Date()) {
        return false
      }
      return true
    }
    return ['active', 'SUPER'].includes(subscription.status) ||
      (subscription.status === 'cancelled' &&
        subscription.next_billing_date &&
        new Date(subscription.next_billing_date) > new Date())
  }

  if (!hasValidSubscription()) {
    // External users with expired trial can still access shared contracts
    const isExternal = userData?.account_type === 'external'
    if (isExternal) {
      const isContractRoute = location.pathname === '/contract' || location.pathname.startsWith('/contract/')
      if (isContractRoute) {
        return children // Allow — list shows shared contracts, right panel locked via lockedTabs
      }
      return <Navigate to="/subscribe" />
    }

    // Expired trial or inactive — prompt to subscribe
    if (subscription?.status === 'inactive' ||
        (subscription?.status === 'trial' && subscription?.trial_end && new Date(subscription.trial_end) < new Date())) {
      return <Navigate to="/subscribe" />
    }
    if (subscription?.status === 'payment_required') {
      return <Navigate to="/payment-failed" />
    }
    // No subscription at all (incomplete signup) — send to subscribe
    if (!subscription) {
      return <Navigate to="/subscribe" />
    }
    return <Navigate to="/settings" />
  }

  // Redirect un-boarded users to profile completion before accessing the app
  if (userData && userData.is_boarded === false) {
    return <Navigate to="/onboarding" />
  }

  // Redirect to getting-started if onboarding tours not completed and user lands on dashboard
  if (location.pathname === '/' && isOnboardingActive) {
    return <Navigate to="/getting-started" replace />
  }

  return children
};


// PrivateRoute component to protect routes based on user authentication
const PrivateRoute = ({ children }) => {
  const user = useSelector(state => state.auth.user)

  return user ? <SubscriptionRoute>{children}</SubscriptionRoute> : <Navigate to='/login' />
}

// OnboardingGuard — authenticated users who already completed onboarding are redirected to /
const OnboardingGuard = ({ children }) => {
  const user = useSelector(state => state.auth.user)
  const userData = user?.user
  const redirectTo = React.useRef(null)

  if (!user) return <Navigate to='/login' />
  if (userData && userData.is_boarded === true) {
    // Lock the redirect destination on the first render — subsequent re-renders
    // (from fetchSubscription, etc.) must not overwrite it after localStorage is cleared.
    if (redirectTo.current === null) {
      const redirectContractId = localStorage.getItem('redirect_after_onboarding')
      if (redirectContractId) {
        localStorage.removeItem('redirect_after_onboarding')
        redirectTo.current = `/contract/${redirectContractId}`
      } else {
        redirectTo.current = '/getting-started'
      }
    }
    return <Navigate to={redirectTo.current} replace />
  }

  return children
}

// FeatureRoute — redirects to dashboard if the user's plan doesn't include the feature
const FeatureRoute = ({ feature, children }) => {
  const { data: subscription } = useSelector(state => state.subscription)
  const userData = useSelector(state => state.auth.user?.user)
  const isSuperAdmin = userData?.roles?.includes('super_admin')

  if (isSuperAdmin) return children

  const features = subscription?.features || {}
  if (features[feature] === true) return children

  return <Navigate to='/' replace />
}

// SuperAdminRoute — only super_admin users can access
const SuperAdminRoute = ({ children }) => {
  const userData = useSelector(state => state.auth?.user?.user)
  const isSuperAdmin = userData?.roles?.includes('super_admin')

  if (!isSuperAdmin) {
    return <Navigate to="/" replace />
  }

  return children
}

// AuthWrapper component to handle token expiration checks
const AuthWrapper = ({ children }) => {
  const user = useSelector(state => state.auth.user)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  // Function to check token expiration
  const isTokenExpired = token => {
    if (!token) return true
    const { exp } = jwtDecode(token)
    return Date.now() >= exp * 1000
  }

  useEffect(() => {
    const fetch_fn = async () => {
      // Skip auto-fetch when a pending checkout is in progress — the signUp action
      // handles auth + Stripe redirect. Fetching here would trigger SubscriptionRoute
      // to redirect to /payment-cancel before the Stripe session is ready.
      if (localStorage.getItem('pendingCheckout')) return

      const token = localStorage.getItem('access_token')
      if (!token) return
      try {
        const decoded = jwtDecode(token)
        const userId = decoded.sub
        if (userId) {
          console.log('fetching user...', userId)
          dispatch(fetchUser(userId))
        }
      } catch (e) {
        console.error('Failed to decode token for fetchUser:', e)
      }
    }
    fetch_fn()
  }, [])

  useEffect(() => {
    // List of allowed public routes that don't require authentication
    const publicRoutes = [
      '/payment-success',
      '/payment-cancel',
      '/payment-failed',
      '/login',
      '/signup',
      '/signup/confirmation',
      '/signup/confirmation-dm',
      '/forgot-password',
      '/password-reset-success',
      '/reset-password',
      '/verify-otp',
      '/invitation',
      '/external-invitation',
      '/not-found-screen',
      '/confirm',
      '/sign'
    ]

    // Special routes that can be accessed by both logged-in and logged-out users
    const specialRoutes = [
      '/checkout',
      '/payment-success',
      '/payment-cancel',
      '/payment-failed',
    ]

    const isPublicRoute = publicRoutes.some(route =>
      location.pathname.startsWith(route)
    )

    const isSpecialRoute = specialRoutes.some(route =>
      location.pathname.startsWith(route)
    )

    if (isPublicRoute && user && !isSpecialRoute) {
      // Redirect logged-in users away from public routes
      navigate('/');
    } else if (!isPublicRoute && !isSpecialRoute) {
      // Redirect to login if user does not exist
      if (!user) {
        navigate('/login')
        return
      }

      // Check token expiration, logout, and redirect if expired
      if (isTokenExpired(user?.token?.access_token)) {
        dispatch(logout())
        navigate('/login')
      }
    }

  }, [user, dispatch, navigate, location.pathname])

  return children
}

// Routes where navbar should be hidden (standalone pages)
const NAVBAR_HIDDEN_ROUTES = ['/payment-success', '/payment-cancel', '/payment-failed', '/onboarding', '/admin']

const NavbarWrapper = () => {
  const user = useSelector(state => state.auth.user)
  const location = useLocation()

  const shouldHide = NAVBAR_HIDDEN_ROUTES.some(route =>
    location.pathname === route || location.pathname.startsWith(route + '/')
  )

  if (!user || shouldHide) return null
  return <Navbar />
}

// Add ScrollToTop component
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Auto-track page views via custom analytics
const PageTracker = () => {
  usePageTracking();
  return null;
};

// MainContent component to handle dynamic padding based on route
const MainContent = ({ children }) => {
  const location = useLocation();
  const user = useSelector(state => state.auth.user);
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down('md'));

  // Check if current route is a chat/conversation route
  const isChatRoute = location.pathname.startsWith('/conversation');

  // Check if current route is a full-height route (manages its own scrolling)
  const isFullHeightRoute = location.pathname === '/contract' || location.pathname.startsWith('/contract/');

  // Check if current route is an auth route
  const authRoutes = ['/login', '/signup', '/forgot-password', '/password-reset-success', '/reset-password', '/verify-otp', '/invitation', '/external-invitation', '/confirm', '/onboarding', '/sign'];
  const isAuthRoute = authRoutes.some(route => location.pathname === route || location.pathname.startsWith(route + '/'));

  // Check if navbar is hidden on this route
  const isNavbarHidden = NAVBAR_HIDDEN_ROUTES.some(route =>
    location.pathname === route || location.pathname.startsWith(route + '/')
  );

  // Use same breakpoint as Navbar (md) to match when AppBar is shown
  const hasMobileNavbar = isMobileView && user && !isNavbarHidden;

  return (
    <div style={{
      flexGrow: 1,
      minWidth: 0,
      height: hasMobileNavbar ? `calc(100% - ${MOBILE_APP_BAR_HEIGHT}px)` : '100%',
      overflowY: (isChatRoute || isFullHeightRoute) ? 'hidden' : 'auto',
      overflowX: 'hidden',
      marginTop: hasMobileNavbar ? `${MOBILE_APP_BAR_HEIGHT}px` : 0,
      // Hide scrollbar while keeping scroll functionality
      scrollbarWidth: 'none', /* Firefox */
      msOverflowStyle: 'none', /* IE/Edge */
      WebkitScrollbar: 'none' /* Chrome/Safari - needs proper syntax below */
    }}
    className="hide-scrollbar">
      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      {children}
    </div>
  );
};

const AppRouter = () => {
  const user = useSelector(state => state.auth.user)
  const dispatch = useDispatch()
  const { appPreferences } = useSelector(state => state.user)
  const { data: subscription } = useSelector(state => state.subscription)

  const userIdForPrefs = user?.user?.id || user?.token?.user?.id
  const subscriptionStatus = subscription?.status
  useEffect(() => {
    // Only fetch preferences when user has a valid subscription (is_allowed=true on backend).
    // Pre-checkout users (is_allowed=false) would get 401 and trigger a logout loop.
    if (userIdForPrefs && subscriptionStatus && ['active', 'trial', 'SUPER'].includes(subscriptionStatus)) {
      dispatch(fetchAppPreferences())
    }
  }, [userIdForPrefs, subscriptionStatus, dispatch])

  // Load user-specific onboarding state
  useEffect(() => {
    const userId = user?.user?.id || user?.token?.user?.id
    if (userId) {
      dispatch(loadOnboarding(userId))
    }
  }, [user, dispatch])

  return (
    <Router>
      <ScrollToTop />
      <PageTracker />
      <AuthWrapper>
        <div style={{ display: 'flex', height: '100%' }}>
          <NavbarWrapper />
          <MainContent>
            <Routes>
              {/* Public Routes */}
              <Route path='/payment-success' element={<PaymentSuccessScreen />} />
              <Route path='/payment-cancel' element={<PaymentCancelScreen />} />
              <Route path='/payment-failed' element={<PaymentFailedScreen />} />
              <Route path='/subscribe' element={<SubscribeScreen />} />

              <Route path='/login' element={<LoginScreen />} />
              <Route path='/verify-otp' element={<OTPVerificationScreen />} />
              <Route
                path='/forgot-password'
                element={<ForgotPasswordScreen />}
              />
              <Route
                path='/password-reset-success'
                element={<PasswordResetSuccessScreen />}
              />
              <Route path='/reset-password' element={<ResetPasswordScreen />} />

              <Route path="/invitation" element={<InvitationScreen />} />
              <Route path="/external-invitation" element={<ExternalInvitationScreen />} />

              {/* Public e-signature signing page — no auth required */}
              <Route path='/sign/:token' element={<SigningScreen />} />

              <Route path='/signup' element={<SignupScreen />} />
              <Route path='/confirm/:token' element={<ConfirmAccount />} />
              <Route
                path='/signup/confirmation'
                element={<SignupConfirmationScreen />}
              />
              <Route
                path='/signup/confirmation-dm'
                element={<SignupConfirmationDomainScreen />}
              />

              {/* Onboarding — requires auth, bypasses subscription gate, blocks already-boarded users */}
              <Route
                path='/onboarding'
                element={
                  <OnboardingGuard>
                    <OnboardingScreen />
                  </OnboardingGuard>
                }
              />

              {/* Private Routes */}
              <Route
                path='/getting-started'
                element={
                  <PrivateRoute>
                    <OnboardingDashboard />
                  </PrivateRoute>
                }
              />
              <Route
                path='/'
                element={
                  <PrivateRoute>
                    <DashboardScreen />
                  </PrivateRoute>
                }
              />
              <Route
                path='/search'
                element={
                  <PrivateRoute>
                    <SearchScreen />
                  </PrivateRoute>
                }
              />
              <Route
                path='/conversation/:conversationId'
                element={
                  <PrivateRoute>
                    <ChatScreen />
                  </PrivateRoute>
                }
              />
              <Route
                path='/conversation'
                element={
                  <PrivateRoute>
                    <ChatScreen />
                  </PrivateRoute>
                }
              />
              <Route
                path='/contract'
                element={
                  <PrivateRoute>
                    <FeatureRoute feature={FEATURES.CONTRACT_MODULE}>
                      <ContractHistory />
                    </FeatureRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path='/contract/dashboard'
                element={
                  <PrivateRoute>
                    <FeatureRoute feature={FEATURES.CONTRACT_MODULE}>
                      <CLMDashboardScreen />
                    </FeatureRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path='/contract/template/:templateId'
                element={
                  <PrivateRoute>
                    <FeatureRoute feature={FEATURES.CONTRACT_MODULE}>
                      <TemplateEditorScreen />
                    </FeatureRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path='/contract/:contractId'
                element={
                  <PrivateRoute>
                    <FeatureRoute feature={FEATURES.CONTRACT_MODULE}>
                      <ContractEditionScreen />
                    </FeatureRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path='/settings'
                element={
                  <PrivateRoute>
                    <SettingsScreen />
                  </PrivateRoute>
                }
              />
              <Route
                path='/welcome'
                element={
                  <PrivateRoute>
                    <WelcomeScreen />
                  </PrivateRoute>
                }
              />
              <Route
                path='/legal-news'
                element={
                  <PrivateRoute>
                    <FeatureRoute feature={FEATURES.NEWS_HUB}>
                      <NewsHubScreen />
                    </FeatureRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path='/legal-news/:newsId'
                element={
                  <PrivateRoute>
                    <FeatureRoute feature={FEATURES.NEWS_HUB}>
                      <NewsDetailScreen />
                    </FeatureRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path='/legal-news/preferences'
                element={
                  <PrivateRoute>
                    <FeatureRoute feature={FEATURES.NEWS_HUB}>
                      <NewsPreferencesScreen />
                    </FeatureRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path='/legal-notice'
                element={
                  <PrivateRoute>
                    <EntreprisesScreen />
                  </PrivateRoute>
                }
              />
              <Route
                path='/legal-notice/annonce/:annonceId'
                element={
                  <PrivateRoute>
                    <AnnonceDetailScreen />
                  </PrivateRoute>
                }
              />
              <Route
                path='/legal-notice/company/:rc'
                element={
                  <PrivateRoute>
                    <CompanyProfileScreen />
                  </PrivateRoute>
                }
              />
              <Route
                path='/admin/*'
                element={
                  <PrivateRoute>
                    <SuperAdminRoute>
                      <AdminPanelScreen />
                    </SuperAdminRoute>
                  </PrivateRoute>
                }
              />
              {/* Redirects for old admin URLs */}
              <Route path='/analytics' element={<Navigate to='/admin/analytics' replace />} />
              <Route path='/data-update' element={<Navigate to='/admin/data-update' replace />} />

              {/* Not Found Route */}
              <Route path='/not-found-screen' element={<NotFoundScreen />} />
              {/* Catch-All Route */}
              <Route path='*' element={<Navigate to='/not-found-screen' />} />
            </Routes>
          </MainContent>
          {user && <FloatingActionButton />}
        </div>
      </AuthWrapper>
    </Router>
  )
}

export default AppRouter